import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Collapse, Form, Input, InputNumber, Select, Table, Tabs, Tag, message } from 'antd';
import type { TabsProps } from 'antd';
import type { TablePaginationConfig } from 'antd/es/table';
import AiCreditService, { AiCreditSale, AiCreditSettings, AiPromptSettings } from '../services/AiCreditService';

const statusLabels: Record<AiCreditSale['status'], { color: string; label: string }> = {
  PENDING: { color: 'gold', label: 'Bekliyor' },
  COMPLETED: { color: 'green', label: 'Tamamlandı' },
  FAILED: { color: 'red', label: 'Başarısız' },
};

const formatCurrency = (amount?: number | null) =>
  new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(Number(amount ?? 0));

const formatDate = (value?: string | null) =>
  value ? new Intl.DateTimeFormat('tr-TR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value)) : '-';

const AiCreditManagement: React.FC = () => {
  const [form] = Form.useForm<AiCreditSettings>();
  const [promptForm] = Form.useForm<AiPromptSettings>();
  const [sales, setSales] = useState<AiCreditSale[]>([]);
  const [salesLoading, setSalesLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingPrompts, setSavingPrompts] = useState(false);
  const [reconcilingId, setReconcilingId] = useState<number | null>(null);
  const [pagination, setPagination] = useState<TablePaginationConfig>({ current: 1, pageSize: 20, total: 0 });

  const loadSettings = useCallback(() => {
    AiCreditService.getSettings().then(({ data }) => {
      if (data.available && data.unitPrice !== null) {
        form.setFieldsValue({ ...data, unitPrice: Number(data.unitPrice) });
      }
    }).catch(() => message.error('Yapay zekâ kredi ayarı yüklenemedi.'));
  }, [form]);

  const loadSales = useCallback((page = 0, size = 20) => {
    setSalesLoading(true);
    AiCreditService.getSales(page, size)
      .then(({ data }) => {
        setSales(data.content);
        setPagination({ current: data.number + 1, pageSize: data.size, total: data.totalElements });
      })
      .catch(() => message.error('Kredi satış geçmişi yüklenemedi.'))
      .finally(() => setSalesLoading(false));
  }, []);

  const loadPrompts = useCallback(() => {
    AiCreditService.getPrompts()
      .then(({ data }) => promptForm.setFieldsValue(data))
      .catch(() => message.error('Yapay zekâ istemleri yüklenemedi.'));
  }, [promptForm]);

  useEffect(() => {
    loadSettings();
    loadPrompts();
    loadSales();
  }, [loadPrompts, loadSales, loadSettings]);

  const saveSettings = (settings: AiCreditSettings) => {
    setSaving(true);
    AiCreditService.updateSettings(settings)
      .then(({ data }) => {
        form.setFieldsValue({ ...data, unitPrice: Number(data.unitPrice) });
        message.success('Yapay zekâ kredi ve model ayarları güncellendi.');
      })
      .catch(() => message.error('Kredi fiyatı güncellenemedi.'))
      .finally(() => setSaving(false));
  };

  const reconcileSale = (sale: AiCreditSale) => {
    setReconcilingId(sale.id);
    AiCreditService.reconcileSale(sale.id)
      .then(() => {
        message.success('Ödeme durumu Iyzico üzerinden yeniden doğrulandı.');
        loadSales((pagination.current ?? 1) - 1, pagination.pageSize ?? 20);
      })
      .catch(() => message.error('Ödeme durumu yeniden doğrulanamadı.'))
      .finally(() => setReconcilingId(null));
  };

  const savePrompts = (settings: AiPromptSettings) => {
    setSavingPrompts(true);
    AiCreditService.updatePrompts(settings)
      .then(({ data }) => {
        promptForm.setFieldsValue(data);
        message.success('Yapay zekâ istemleri güncellendi.');
      })
      .catch(() => message.error('Yapay zekâ istemleri güncellenemedi.'))
      .finally(() => setSavingPrompts(false));
  };

  const promptTabs: TabsProps['items'] = [
    {
      key: 'student-chat',
      label: 'Öğrenci Sohbeti',
      forceRender: true,
      children: (
        <Form.Item
          name="chatSystemMessage"
          label="Sistem istemi"
          extra="Bu metin, öğrenci sohbeti isteklerinin sistem talimatı olarak kullanılır."
          rules={[{ required: true, whitespace: true, message: 'Sistem istemi zorunludur.' }]}
        >
          <Input.TextArea autoSize={{ minRows: 12, maxRows: 24 }} />
        </Form.Item>
      ),
    },
    {
      key: 'student-report',
      label: 'Öğrenci İhtiyaç ve Gelişim Raporu',
      forceRender: true,
      children: (
        <Form.Item
          name="studentReportSystemMessage"
          label="Sistem istemi"
          extra="Bu metin, öğrenci sohbetlerinden üretilen ihtiyaç ve gelişim raporlarında kullanılır."
          rules={[{ required: true, whitespace: true, message: 'Sistem istemi zorunludur.' }]}
        >
          <Input.TextArea autoSize={{ minRows: 8, maxRows: 20 }} />
        </Form.Item>
      ),
    },
    {
      key: 'weekly-report',
      label: 'Haftalık Etkinlik Raporu',
      forceRender: true,
      children: (
        <Form.Item
          name="weeklyActivityReportSystemMessage"
          label="Sistem istemi"
          extra="Bu metin, öğrencinin haftalık etkinlik raporlarında kullanılır."
          rules={[{ required: true, whitespace: true, message: 'Sistem istemi zorunludur.' }]}
        >
          <Input.TextArea autoSize={{ minRows: 8, maxRows: 20 }} />
        </Form.Item>
      ),
    },
  ];

  const columns = [
    { title: 'Öğrenci', dataIndex: 'studentName', key: 'studentName', render: (name: string, record: AiCreditSale) => <div><div>{name}</div><small>{record.studentEmail}</small></div> },
    { title: 'Kredi', dataIndex: 'creditAmount', key: 'creditAmount' },
    { title: 'Birim Fiyat', dataIndex: 'unitPrice', key: 'unitPrice', render: formatCurrency },
    { title: 'Toplam', dataIndex: 'totalAmount', key: 'totalAmount', render: formatCurrency },
    { title: 'Durum', dataIndex: 'status', key: 'status', render: (status: AiCreditSale['status']) => <Tag color={statusLabels[status].color}>{statusLabels[status].label}</Tag> },
    { title: 'İşlem Kimliği', dataIndex: 'paymentTransactionId', key: 'paymentTransactionId', ellipsis: true },
    { title: 'Tarih', dataIndex: 'createdAt', key: 'createdAt', render: formatDate },
    {
      title: 'İşlem',
      key: 'action',
      render: (_: unknown, sale: AiCreditSale) => sale.status === 'PENDING' ? (
        <Button
          size="small"
          onClick={() => reconcileSale(sale)}
          loading={reconcilingId === sale.id}
          disabled={!sale.reconcilable}
          title={sale.reconcilable ? undefined : 'Bu eski işlem için Iyzico doğrulama anahtarı bulunmuyor.'}
        >
          Yeniden doğrula
        </Button>
      ) : '-',
    },
  ];

  return (
    <div>
      <Collapse
        style={{ marginBottom: 16 }}
        items={[{
          key: 'ai-credit-settings',
          label: 'Yapay Zekâ Sohbet Kredisi Ayarı',
          forceRender: true,
          children: (
            <Form form={form} layout="vertical" initialValues={{ unitPrice: 1, model: 'GPT_5_6_SOL', serviceTier: 'DEFAULT', reasoningEffort: 'MEDIUM' }} onFinish={saveSettings}>
              <Form.Item name="unitPrice" label="1 kredi fiyatı" rules={[{ required: true, message: 'Birim fiyat zorunludur.' }]}>
                <InputNumber min={0.01} precision={2} step={0.01} addonAfter="₺" />
              </Form.Item>
              <Form.Item name="model" label="Model" rules={[{ required: true, message: 'Model zorunludur.' }]}>
                <Select options={[{ value: 'GPT_5_6_SOL', label: 'GPT-5.6 Sol' }, { value: 'GPT_5_6_TERRA', label: 'GPT-5.6 Terra' }, { value: 'GPT_5_6_LUNA', label: 'GPT-5.6 Luna' }]} />
              </Form.Item>
              <Form.Item name="serviceTier" label="Hız" rules={[{ required: true, message: 'Hız seçimi zorunludur.' }]}>
                <Select options={[{ value: 'DEFAULT', label: 'Varsayılan' }, { value: 'PRIORITY', label: 'Hızlı' }, { value: 'FLEX', label: 'Esnek' }]} />
              </Form.Item>
              <Form.Item name="reasoningEffort" label="Muhakeme çabası" rules={[{ required: true, message: 'Muhakeme çabası zorunludur.' }]}>
                <Select options={[{ value: 'NONE', label: 'Yok' }, { value: 'LOW', label: 'Düşük' }, { value: 'MEDIUM', label: 'Orta' }, { value: 'HIGH', label: 'Yüksek' }, { value: 'XHIGH', label: 'Çok yüksek' }, { value: 'MAX', label: 'En yüksek' }]} />
              </Form.Item>
              <Form.Item style={{ marginBottom: 0 }}>
                <Button type="primary" htmlType="submit" loading={saving}>Kaydet</Button>
              </Form.Item>
            </Form>
          ),
        }]}
      />
      <Collapse
        style={{ marginBottom: 16 }}
        items={[{
          key: 'ai-prompt-settings',
          label: 'Yapay Zekâ İstemleri',
          forceRender: true,
          children: (
            <Form form={promptForm} layout="vertical" onFinish={savePrompts}>
              <Tabs items={promptTabs} />
              <Form.Item style={{ marginBottom: 0 }}>
                <Button type="primary" htmlType="submit" loading={savingPrompts}>İstemleri kaydet</Button>
              </Form.Item>
            </Form>
          ),
        }]}
      />
      <Card title="Yapay Zekâ Kredisi Satış Geçmişi">
        <Table<AiCreditSale>
          rowKey="id"
          columns={columns}
          dataSource={sales}
          loading={salesLoading}
          scroll={{ x: 1050 }}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            onChange: (page, size) => loadSales(page - 1, size),
          }}
        />
      </Card>
    </div>
  );
};

export default AiCreditManagement;
